import './style.scss';

// Footer wordmark scroll motion.
//
// The mark has two rest states and both are measured from the live layout,
// never from a fixed pixel distance:
//
//   down : the bottom of the (g) tail sits on the top edge of the bottom
//          nav row
//   up   : the top of the (b) sits on the bottom edge of the top nav row
//
// The two long strokes - the (r) descender and the (d) ascender - are meant
// to bleed straight past those anchors and get clipped by the footer, so
// they are excluded from the anchor measurement (that is what the
// data-wm hooks in the wordmark SVG are for). "Letter body" below means the
// (b) top to (g) tail band, i.e. the part that has to stay between the navs.
//
// While it travels the whole mark stretches vertically. scaleY is driven
// from the slope of the easing curve, so the stretch is a function of how
// fast the mark is actually moving rather than a separately authored pulse,
// and it relaxes back to 1 at both rest states. The same curve runs in both
// directions - one mirrored tween, elastic.out(1, 0.55) either way - so the
// way back feels identical to the way there instead of an eased-in reverse.
//
// The mark's transform is written by hand (see paint) instead of being set
// through GSAP. GSAP is still what drives the timing, but the matrix has to
// be arithmetic this file owns, because turning round mid-travel means
// handing three things over without a jump:
//
//   * y - the tween simply starts from wherever the mark had got to.
//   * scaleY - carried into the new tween and blended into the new curve's
//     stretch over STRETCH_ATTACK, rather than dropping to 1 on frame one.
//   * the pivot, which moves with the direction of travel. A stretched mark
//     paints its letter body at y + pivot + scaleY * (u - pivot), so moving
//     the pivot from p to q has to give y back (p - q) * (1 - scaleY) to stay
//     put. At rest scaleY is 1, that term is zero, and the rest states are
//     exactly where they were measured.

const EASE = 'elastic.out(1, 0.55)';
// Blended into the curve, and only ever partly, when a turnaround leaves the
// mark further to travel than the nav-to-nav span it was tuned against. Both
// curves run 0 -> 1, so the blend changes the rebound and nothing else.
const REBOUND_DAMP_EASE = 'power2.out';
const TRAVEL_DURATION = 1.15;

// Peak stretch is 1 + MAX_STRETCH, reached where the curve is steepest.
const MAX_STRETCH = .28;
// Ease slope -> stretch. elastic.out(1, 0.55) tops out near 7, so this
// gain puts the peak slightly over MAX_STRETCH and the clamp trims it.
const STRETCH_GAIN = .045;
// Progress window the stretch is blended in over: from a standing start that
// keeps the first frame off the peak, and on a mid-travel turnaround it is
// what carries the stretch already on the mark into the new curve.
const STRETCH_ATTACK = .06;
// Central difference step used to read the slope off the easing curve.
const SLOPE_STEP = 1 / 240;

// Below this many on-screen px there is not enough room between the navs to
// be worth moving.
const MIN_TRAVEL = 24;

// How far past a rest state the mark is allowed to swing, in on-screen px.
//
// Both rest states are measured flush against a nav row, so an overshoot is
// the mark moving INTO that row - which is exactly the overlap the client
// reported at both ends, on desktop and on mobile. The rebound is what is
// wrong, not the rest states ("the final position is correct just the force
// of overlap"), so the curve is damped until its peak fits this budget
// instead of the rest states being pushed apart.
//
// Expressed in px, not as a fraction of the travel, because that is what the
// overlap is: elastic.out(1, 0.55) peaks ~19% past its target, which is a few
// px on a short viewport and tens of px on a tall one - the same curve, and
// only the tall one collides.
const MAX_OVERSHOOT = 6;
// Sampling resolution used to find a curve's peak. 1/240 matches SLOPE_STEP:
// at TRAVEL_DURATION this is finer than a frame, so the sampled peak is the
// peak that actually gets painted.
const PEAK_STEP = 1 / 240;
// Bisection steps for the damping factor. 12 halvings resolve k to ~0.0002,
// far below what a px budget can distinguish.
const DAMP_STEPS = 12;

function initFooterWordmark(footer) {
	const {gsap, ScrollTrigger} = window.rebredgVendors || {};

	if (!gsap || !ScrollTrigger) {
		return;
	}

	if (footer.dataset.rebredgFooterMarkReady === 'true') {
		return;
	}

	const svg = footer.querySelector('.rebredg-footer-wordmark-svg');
	const mark = footer.querySelector('.rebredg-footer-wordmark-mark');
	const navTop = footer.querySelector('.rebredg-footer-top');
	const navBottom = footer.querySelector('.rebredg-footer-bottom');
	const bodyTop = svg && svg.querySelector('[data-wm="b"]');
	const bodyBottom = svg && svg.querySelector('[data-wm="g-tail"]');

	if (!svg || !mark || !navTop || !navBottom || !bodyTop || !bodyBottom) {
		return;
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		return;
	}

	footer.dataset.rebredgFooterMarkReady = 'true';

	gsap.registerPlugin(ScrollTrigger);
	ScrollTrigger.config({ignoreMobileResize: true});

	const ease = gsap.parseEase(EASE);
	const dampEase = gsap.parseEase(REBOUND_DAMP_EASE);

	let rest = {scale: 1, up: 0, down: 0, originTop: 0, originBottom: 0, originX: 0};
	let current = 0;
	let currentScale = 1;
	let currentOrigin = 0;
	let tween = null;
	let raised = false;
	let enabled = true;
	// paint() runs once inside measure() with `rest` still holding the previous
	// (or the initial, all-zero) measurement, so the clamp has to stay out of
	// the way until a real measurement has landed.
	let clampReady = false;

	// Both rest offsets are read straight off the painted anchors with the
	// mark's transform off - one forced layout on refresh, put back in the
	// same frame - so nothing here has to work out where the artwork's
	// viewBox lands inside its box.
	//
	// The offsets come out in artwork units, not px: a transform on an SVG
	// element is applied inside the viewBox, so a y of 100 moves the mark
	// 100 * scale on screen. scale is taken off a glyph's own width, which
	// the animation never touches.
	function measure() {
		clampReady = false;
		mark.removeAttribute('transform');

		const topBox = bodyTop.getBBox();
		const bottomBox = bodyBottom.getBBox();
		const markBox = mark.getBBox();
		const topRect = bodyTop.getBoundingClientRect();
		const bottomRect = bodyBottom.getBoundingClientRect();
		const scale = topBox.width ? topRect.width / topBox.width : 0;
		const up = navTop.getBoundingClientRect().bottom - topRect.top;
		const down = navBottom.getBoundingClientRect().top - bottomRect.bottom;

		paint();

		if (!scale || !markBox.width) {
			return null;
		}

		return {
			scale,
			up: up / scale,
			down: down / scale,
			// Stretch pivots on the letter body, not on the mark's own box -
			// that box is dominated by the two bleeding strokes and scaling
			// around it would throw the body clean out of the footer.
			originTop: topBox.y,
			originBottom: bottomBox.y + bottomBox.height,
			originX: markBox.x + markBox.width / 2,
		};
	}

	// Stretch that would push the mark into a nav row, trimmed back.
	//
	// Damping the travel curve only bounds where the mark's body would sit at
	// scaleY 1. The stretch then adds to that, and it adds on the leading edge
	// - the mark elongates INTO the direction it is travelling - so on a short
	// viewport, where the body is large next to the nav-to-nav gap, peak
	// stretch alone put tens of px of the wordmark over the nav text. That, not
	// the rebound, was most of the overlap.
	//
	// The stretch is trimmed rather than capped up front: it is at its peak
	// mid-travel, where there is room for all of it, and only has to give way
	// near the ends. So the leading edge is held to the same px budget as the
	// rebound, and the scale is solved backwards from that limit - full stretch
	// wherever it fits, less only where it would not.
	//
	// Both edges are solved and the smaller scale wins. Only the leading one
	// can actually bind: the pivot sits on the trailing edge, which the scale
	// therefore does not move (its term is multiplied by zero).
	function paintScale() {
		if (!clampReady) {
			return currentScale;
		}

		const budget = MAX_OVERSHOOT / rest.scale;
		const topLimit = rest.up + rest.originTop - budget;
		const bottomLimit = rest.down + rest.originBottom + budget;
		const toTop = rest.originTop - currentOrigin;
		const toBottom = rest.originBottom - currentOrigin;
		let scale = currentScale;

		// top edge  = current + origin + scale * toTop     >= topLimit
		if (toTop < 0) {
			scale = Math.min(scale, (topLimit - current - currentOrigin) / toTop);
		}

		// bottom edge = current + origin + scale * toBottom <= bottomLimit
		if (toBottom > 0) {
			scale = Math.min(scale, (bottomLimit - current - currentOrigin) / toBottom);
		}

		// Never below 1: the budget is there to stop the mark growing into a
		// nav row, not to squash it when the travel alone has used the budget
		// up (the damped curve keeps that within the same 6px).
		return Math.max(1, scale);
	}

	// One matrix, in artwork units: shift by y, then scale about the pivot.
	// At scaleY 1 it reduces to a plain translate, which is what makes the
	// measured rest offsets land exactly.
	function paint() {
		mark.setAttribute(
			'transform',
			`translate(0 ${current}) translate(0 ${currentOrigin}) scale(1 ${paintScale()}) translate(0 ${-currentOrigin})`
		);
	}

	function settle(y) {
		current = y;
		currentScale = 1;
		paint();
	}

	// Blend of the elastic and a curve with no rebound at all. k is how much of
	// the elastic survives: 1 is the raw elastic, 0 lands with no overshoot.
	// Both run 0 -> 1, so this changes the rebound and nothing else.
	function blend(k) {
		return k >= 1
			? ease
			: (progress) => ease(progress) * k + dampEase(progress) * (1 - k);
	}

	// Largest value a curve reaches above its target, as a fraction of the
	// travel. Sampled rather than derived: the blend of two easings has no
	// closed form for its peak, and the blend's peak sits at a different
	// progress than the elastic's own.
	function peakOf(curve) {
		let peak = 0;

		for (let progress = 0; progress <= 1; progress += PEAK_STEP) {
			peak = Math.max(peak, curve(progress) - 1);
		}

		return peak;
	}

	// The strongest rebound that still lands inside the px budget.
	//
	// Two separate things ask for damping and the tighter one wins:
	//
	//   * the overshoot budget - a tall viewport puts the navs far enough
	//     apart that 19% of the travel is a large overlap, a short one does
	//     not, so the same curve has to be damped by different amounts.
	//   * a mid-travel turnaround, which starts the mark out in the previous
	//     move's rebound and therefore further from its target than a
	//     nav-to-nav move. Left alone the curve would rebound proportionally
	//     further and throw the mark past the nav; scaling it back to the
	//     distance it was tuned against keeps the overshoot the same size
	//     however often the direction is flipped.
	//
	// Monotone in k (more elastic, more overshoot), so a bisection finds the
	// largest k that fits.
	function curveFor(travel) {
		const span = rest.down - rest.up;
		const turnaroundK = span > 0 && travel > span ? span / travel : 1;
		const travelPx = travel * rest.scale;

		if (travelPx <= 0) {
			return blend(turnaroundK);
		}

		const budget = MAX_OVERSHOOT / travelPx;

		if (peakOf(blend(turnaroundK)) <= budget) {
			return blend(turnaroundK);
		}

		let low = 0;
		let high = turnaroundK;

		for (let step = 0; step < DAMP_STEPS; step += 1) {
			const mid = (low + high) / 2;

			if (peakOf(blend(mid)) <= budget) {
				low = mid;
			} else {
				high = mid;
			}
		}

		return blend(low);
	}

	// scaleY at a point on the curve: fast movement stretches, and whatever
	// stretch the mark already carries is blended out over the attack window
	// so a turnaround does not drop it on the first frame.
	function stretchAt(curve, progress, fromScale) {
		const slope = (curve(Math.min(1, progress + SLOPE_STEP)) - curve(Math.max(0, progress - SLOPE_STEP)))
			/ (2 * SLOPE_STEP);
		const driven = 1 + Math.min(MAX_STRETCH, Math.abs(slope) * STRETCH_GAIN);
		const blend = Math.min(1, progress / STRETCH_ATTACK);

		return fromScale + (driven - fromScale) * blend;
	}

	function travelTo(to, originUnit) {
		if (tween) {
			tween.kill();
			tween = null;
		}

		const fromScale = currentScale;

		current += (currentOrigin - originUnit) * (1 - fromScale);
		currentOrigin = originUnit;

		const from = current;

		if (Math.abs(to - from) < 1) {
			settle(to);
			return;
		}

		paint();

		// Linear playhead with the easing applied by hand, so the same curve
		// drives both the position and the stretch that reads off its slope.
		const curve = curveFor(Math.abs(to - from));
		const playhead = {progress: 0};

		tween = gsap.to(playhead, {
			progress: 1,
			duration: TRAVEL_DURATION,
			ease: 'none',
			onUpdate() {
				const progress = playhead.progress;

				current = from + (to - from) * curve(progress);
				currentScale = stretchAt(curve, progress, fromScale);

				paint();
			},
			onComplete() {
				settle(to);
			},
		});
	}

	// Pulled up: the body's bottom edge trails, so the mark elongates upwards
	// into the direction of travel. Mirrored on the way down.
	const goUp = () => {
		raised = true;

		if (enabled) {
			travelTo(rest.up, rest.originBottom);
		}
	};
	const goDown = () => {
		raised = false;

		if (enabled) {
			travelTo(rest.down, rest.originTop);
		}
	};

	function relayout(self) {
		if (tween) {
			tween.kill();
			tween = null;
		}

		const next = measure();

		if (!next) {
			return;
		}

		rest = next;
		clampReady = true;
		enabled = (rest.down - rest.up) * rest.scale >= MIN_TRAVEL;

		if (!enabled) {
			settle(0);
			return;
		}

		// Read the state off the scroll position rather than off isActive:
		// the trigger stops being active once the last of the footer has been
		// scrolled through, and the mark has to stay up for that stretch.
		if (self) {
			raised = self.scroll() >= self.start;
		}

		settle(raised ? rest.up : rest.down);
	}

	// No pin. The footer carries a short run of its own scroll past the point
	// where it fills the viewport (see $footer-scroll-tail in style.scss), so
	// 'top top' is a real crossing with roughly a pin's worth of scroll behind
	// it - and unlike a pin spacer that short, it leaves more footer under the
	// footer rather than a gap.
	const trigger = ScrollTrigger.create({
		trigger: footer,
		start: 'top top',
		end: 'bottom bottom',
		invalidateOnRefresh: true,
		onRefresh: relayout,
		onEnter: goUp,
		onLeaveBack: goDown,
	});

	relayout(trigger);
}

export function footer_block(root = document) {
	const footer = root.querySelector('[data-site-footer]');
	if (!footer || footer.dataset.rebredgFooterReady === 'true') return;

	footer.dataset.rebredgFooterReady = 'true';

	initFooterWordmark(footer);

	const backTopButton = footer.querySelector('.rebredg-footer-back-top');
	if (!backTopButton) return;

	backTopButton.addEventListener('click', () => {
		window.scrollTo({
			top: 0,
			behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
		});
	});
}
